/**
 * The landing's routes, shared by both shells (see `App.tsx`): the landing
 * shell serves them on a first visit, the app shell once it has loaded.
 */
import React from 'react';
import { Navigate, Route } from 'react-router';

import { ONBOARDING_PATH } from '@/auth/better/post-auth-path';
import { useAuth } from '@/auth/better/session';
import { PageLoader } from '@/components/loading/page-loader';

import { Seo } from './metadata/seo';
// Eagerly load the landing page to prevent Suspense loading flash
import LandingV3Page from './pages/landing-v3/landing-v3-page';
import { LANDING_SUBPAGES, RETIRED_MARKETING_PATHS } from './seo/marketing-routes';

export { LandingV3Page };
export const AboutPage = React.lazy(() => import('./pages/landing-v3/about-page'));

/** The landing shell's routes for one language group: absolute paths for the
 *  English root, relative ones under `/:lang` (where the bare prefix lands on
 *  that language's landing). */
export const renderLandingShellRoutes = (prefixed: boolean) => {
  const toPath = (p: string) => (prefixed ? p.replace(/^\//, '') : p);
  return (
    <>
      {prefixed && <Route index element={<Navigate to="home" replace />} />}
      <Route
        path={toPath('/home')}
        element={
          <>
            <Seo pageKey="home" />
            <LandingV3Page />
          </>
        }
      />
      <Route
        path={toPath('/home/about')}
        element={
          <>
            <Seo pageKey="about" />
            <AboutPage />
          </>
        }
      />
      {/* A feature's or section's own URL: the same landing, opened on its
          card or section (`useLandingCardArrival` / `useLandingSectionArrival`),
          with its own head. */}
      {LANDING_SUBPAGES.map(({ path, seoKey }) => (
        <Route
          key={path}
          path={toPath(path)}
          element={
            <>
              <Seo pageKey={seoKey} />
              <LandingV3Page />
            </>
          }
        />
      ))}
      {/* Old pages with no feature of their own (301 at the edge too). */}
      {RETIRED_MARKETING_PATHS.map((path) => (
        <Route
          key={path}
          path={toPath(path)}
          element={<Navigate to={prefixed ? '..' : '/home'} replace />}
        />
      ))}
    </>
  );
};

// Redirect to dashboard if user is auth-ed
export const RootRedirect = () => {
  const { isSignedIn, isLoaded } = useAuth();
  // Nothing route-specific until auth state is loaded — this prevents a
  // landing flash during the sign-in transition. The page loader carries on
  // from the boot loader meanwhile (invisible on a fast check).
  if (!isLoaded) {
    return <PageLoader variant="screen" />;
  }

  // Redirect to dashboard if signed in
  if (isSignedIn) {
    // A redeem code stashed on /redeem/:code while signed out: the sign-UP
    // handoff loses the redirect URL and lands fresh accounts here,
    // so route them back to the redeem dialog. The redeem page clears it.
    const pendingRedeemCode = localStorage.getItem('pendingRedeemCode');
    if (pendingRedeemCode) {
      return <Navigate to={`/redeem/${encodeURIComponent(pendingRedeemCode)}`} replace />;
    }
    // Through the gate, not straight to the dashboard: a signed-in visitor
    // who still has to onboard sees the flow, never the dashboard first.
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  // Show landing page for unauthenticated users — with the landing's own head
  // (title, canonical → /home, hreflang, structured data): without it `/`
  // kept only the defaults once the static SEO head was stripped on mount.
  return (
    <>
      <Seo pageKey="home" />
      <LandingV3Page />
    </>
  );
};
